import type { ContactModelV2 } from 'Models/ContactModelV2';
import type { CallSession } from 'Modules/softphone/types';
import { isPhoneNumberString } from 'Modules/softphone/utils/validation';
import type { PersonStore } from 'Stores/PersonStore';

export type CallContactSource = Pick<
  CallSession,
  'personId' | 'displayName' | 'phoneNumber'
>;

export interface ContactInfo {
  contact: ContactModelV2 | null;
  displayName: string | undefined;
  phoneNumber: string | undefined;
  avatarUrl: string | undefined;
}

/**
 * Resolves contact information from PersonStore
 * Can be used in both hooks and components
 *
 * @param personStore - The PersonStore instance
 * @param phoneNumber - The phone number to lookup
 * @param fallbackDisplayName - Optional fallback display name
 * @returns Contact information including avatarUrl
 */
export const resolveContactInfo = (
  personStore: PersonStore | null | undefined,
  phoneNumber: string | undefined,
  fallbackDisplayName?: string
): ContactInfo => {
  if (!personStore || !phoneNumber) {
    return {
      contact: null,
      displayName: fallbackDisplayName,
      phoneNumber,
      avatarUrl: undefined,
    };
  }

  // Get contact by phone number
  const contactsPbo = personStore.getAllContactsWithSameNum(phoneNumber);
  const contact: ContactModelV2 | null =
    contactsPbo?.state === 'fulfilled' && contactsPbo.value.length > 0
      ? contactsPbo.value[0]
      : null;

  return {
    contact,
    displayName: contact?.name || fallbackDisplayName,
    phoneNumber: contact?.phoneNumber || phoneNumber,
    avatarUrl: contact?.avatarUrl || undefined,
  };
};

/**
 * Who a call is with. Shared by the header and the announcer so the visible and
 * spoken names cannot drift apart, and takes the call rather than reading the
 * store, because the announcer runs before React has re-rendered.
 */
export const resolveCallContactInfo = (
  personStore: PersonStore | null | undefined,
  call: CallContactSource | null | undefined
): ContactInfo => {
  if (call?.personId) {
    const person = personStore?.selectPersonValueById(Number(call.personId));

    if (person) {
      return {
        contact: person,
        displayName: person.name || undefined,
        phoneNumber: person.phoneNumber || undefined,
        avatarUrl: person.avatarUrl || undefined,
      };
    }
  }

  const fallbackDisplayName = isPhoneNumberString(call?.displayName)
    ? undefined
    : call?.displayName;

  return resolveContactInfo(personStore, call?.phoneNumber, fallbackDisplayName);
};
